import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import PersonIcon from '@mui/icons-material/Person'

import { GridCard, GridList } from 'src/components'
import { ChangeRoleModal } from './change-role-modal.component'
import { TrashTimeoutIcon } from 'src/assets/icons'
import {
  useCancelAccountUsersRemove,
  useMe,
  useNotification,
  useRemoveAccountUsers,
  useResendAccountUserInvite,
} from 'src/hooks'
import { EAccountStatuses, IAccountUserResponseDto } from 'src/types'
import { AppEvents } from 'src/constants'

import { hasResendExpired } from '../utils'

interface GridViewProps {
  data?: void | IAccountUserResponseDto[] | null
}

const GridView = ({ data: users }: GridViewProps) => {
  const [t] = useTranslation()
  const { mutateAsync: removeUser } = useRemoveAccountUsers()
  const { mutateAsync: cancelRemove } = useCancelAccountUsersRemove()
  const { data: me } = useMe()
  const [open, setOpen] = useState(false)
  const { notify } = useNotification()
  const [modalProps, setModalProps] = useState({ email: '', id: '' })
  const { mutate: resendInvite } = useResendAccountUserInvite()

  const handleDelete = (id: string) => async () => {
    const resp = await removeUser([id])

    if (!resp) return

    notify({
      message: t('users-delete-notification.v'),
      icon: <TrashTimeoutIcon />,
      onButtonClick: () => {
        cancelRemove({ ids: [id] })
      },
      timeout: 14000,
    })
  }

  const handleRoleEdit = (email: string, id: string) => () => {
    if (!me || me.id === id) {
      const detail = { message: t('users-change-role-warning.v') }

      const event = new CustomEvent(AppEvents.HTTP_ERROR, { detail })
      return document.dispatchEvent(event)
    }

    setModalProps({ email, id })
    setOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
  }

  const handleResend = (userId: string) => () => {
    resendInvite(userId)
  }

  const createPopperItems = (
    id: string,
    userId: string,
    email: string,
    status: EAccountStatuses,
    inviteSentAt: Date | null,
  ) => [
    {
      title: t('delete-confirm-submit-button.v'),
      onClick: handleDelete(id),
    },
    {
      title: t('account-users-button-1.v'),
      onClick: handleRoleEdit(email, userId),
    },
    {
      title: t('account-users-button-2.v'),
      onClick: handleResend(userId),
      disabled: status !== EAccountStatuses.pending || !hasResendExpired(inviteSentAt),
    },
  ]

  return (
    <>
      <GridList>
        {users?.map(({ id, user, roles, status, activation_token_sent_at }) => (
          <GridCard
            key={id}
            code={roles.join(', ')}
            image={user.image_medium || user.image}
            name={user.email}
            imageNode={!user.image ? PersonIcon : null}
            description={status}
            items={createPopperItems(id, user.id, user.email, status, activation_token_sent_at)}
          />
        ))}
      </GridList>
      <ChangeRoleModal handleClose={handleClose} open={open} {...modalProps} />
    </>
  )
}

export { GridView }
